/**
 * «Обзор» карточки хозяйства (AK-746, wireframe экран 1).
 *
 * Отвечает на три вопроса директора хозяйства подряд: сколько у меня земли,
 * разложена ли она по плану, где данные врут. Ничего считающего здесь нет —
 * все цифры приезжают со сводки одним запросом, чтобы соседние блоки не могли
 * разойтись между собой.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Stack } from '@/components/ui/stack';
import { cn } from '@/lib/utils';
import type { FarmHealthSignal, FarmSummary } from './types';

const HA = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

export function formatHa(value: number): string {
  return `${HA.format(value)} га`;
}

/**
 * Расхождение учётной и контурной площади словами.
 *
 * Показываем ТОЛЬКО когда оно есть: строка «по контуру столько же» — шум,
 * который приучает не читать блок целиком.
 */
export function contourNote(bookHa: number, contourHa: number): string | null {
  const delta = Math.round(contourHa - bookHa);
  if (delta === 0) return null;
  return `по контуру ${HA.format(contourHa)} · ${delta > 0 ? '+' : '−'}${HA.format(Math.abs(delta))}`;
}

const SIGNAL_DOT: Record<FarmHealthSignal['severity'], string> = {
  ok: 'bg-muted-foreground/40',
  warn: 'bg-accent-brand',
  alert: 'bg-destructive',
};

export function OverviewTab({
  summary,
  onOpenIssue,
  onOpenPlan,
}: {
  summary: FarmSummary;
  onOpenIssue: (issue: string) => void;
  onOpenPlan: () => void;
}) {
  const plan = summary.plan;
  const coverage =
    plan.scenario_id && plan.arable_ha > 0
      ? Math.round((plan.planned_ha / plan.arable_ha) * 100)
      : null;
  const note = contourNote(summary.total_ha, summary.contour_total_ha);

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card>
        <CardHeader className="p-4 pb-3">
          <CardTitle className="text-sm">Площади, учётные</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <Stack gap={0}>
            {summary.areas.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Полей ещё нет — заводятся на карте кнопкой «＋ Поле».
              </p>
            )}
            {summary.areas.map((row) => (
              <div
                key={row.code}
                className="flex min-h-10 items-center justify-between gap-4 border-b border-border py-2 text-sm leading-6 last:border-b-0"
              >
                <span>{row.name}</span>
                <span className="font-semibold tabular-nums">{formatHa(row.area_ha)}</span>
              </div>
            ))}
            <div className="flex min-h-10 items-center justify-between gap-4 py-2 text-sm leading-6">
              <span className="text-muted-foreground">Итого</span>
              <span className="font-semibold tabular-nums">{formatHa(summary.total_ha)}</span>
            </div>
            {note && (
              // Контурная площадь — справка, а не вторая правда: расхождение
              // это сигнал к работе с землёй, а не ошибка системы.
              <p className="text-xs text-muted-foreground">{note}</p>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-4 pb-3">
          <CardTitle className="text-sm">
            Покрытие планом{plan.season_year ? ` ${plan.season_year}` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {plan.scenario_id === null ? (
            // Не «0 %»: считать не по чему. Хозяйство без варианта плана не
            // должно выглядеть как хозяйство с проваленным планом.
            <Stack gap={3}>
              <p className="text-sm text-muted-foreground">
                Вариант плана не выбран — считать покрытие пока не по чему.
              </p>
              <Button variant="outline" size="sm" onClick={onOpenPlan}>
                Открыть планирование
              </Button>
            </Stack>
          ) : (
            <Stack gap={3}>
              <p className="text-sm text-muted-foreground">
                В плане{' '}
                <span className="font-semibold text-foreground">{plan.fields_planned}</span> из{' '}
                {plan.fields_total} полей пашни
              </p>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-primary"
                  style={{ width: `${Math.min(coverage ?? 0, 100)}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {formatHa(plan.planned_ha)} из {formatHa(plan.arable_ha)} пашни
                {coverage !== null && (
                  <>
                    {' — '}
                    <span className="font-semibold text-foreground">{coverage}%</span>
                  </>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                Вариант: {plan.scenario_name}
              </p>
              <Button variant="outline" size="sm" onClick={onOpenPlan}>
                Открыть план{plan.season_year ? ` ${plan.season_year}` : ''}
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-4 pb-3">
          <CardTitle className="text-sm">Здоровье данных</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <Stack gap={0}>
            {summary.health.map((signal) => {
              const clickable = signal.issue !== null && signal.count > 0;
              const body = (
                <>
                  <span
                    className={cn(
                      'mt-2 h-2 w-2 shrink-0 rounded-full',
                      SIGNAL_DOT[signal.severity],
                    )}
                  />
                  <span className="flex-1">
                    <span
                      className={cn(
                        'block text-sm font-semibold',
                        clickable && 'text-primary underline',
                      )}
                    >
                      {signal.count > 0 ? `${signal.count} · ` : ''}
                      {signal.title}
                    </span>
                    <span className="block text-xs text-muted-foreground">{signal.hint}</span>
                  </span>
                </>
              );
              // Счётчик без адреса бесполезен: сигнал ведёт в отфильтрованный
              // список полей ЭТОЙ же карточки, а не на общий экран.
              return clickable ? (
                <button
                  key={signal.code}
                  type="button"
                  data-testid={`health-${signal.code}`}
                  onClick={() => onOpenIssue(signal.issue as string)}
                  className="flex w-full items-start gap-2 border-b border-border py-2 text-left last:border-b-0"
                >
                  {body}
                </button>
              ) : (
                <div
                  key={signal.code}
                  className="flex items-start gap-2 border-b border-border py-2 last:border-b-0"
                >
                  {body}
                </div>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    </div>
  );
}
